import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Heart, Lock, Mail } from "lucide-react";
import api from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

export default function Login({ onLogin }) {
  usePageTitle("Login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(`/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      }, {
        withCredentials: true
      });

      toast.success("Welcome back!");
      onLogin(response.data.user, response.data.tenant, response.data.centers, response.data.access_token);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1715775491904-ef5cdae40f0f?q=80&w=1200')"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-sage-600/90 to-sage-800/90" />
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-2 mb-8">
            <img src="/connected-logo.png" alt="ConnectEd" className="h-20" />
          </div>
          <h2 className="text-5xl font-heading font-bold leading-tight mb-6">
            Nurturing Growth,<br />Connecting Communities
          </h2>
          <p className="text-xl text-white/90 leading-relaxed max-w-md">
            The modern childcare management platform designed for multi-center operations.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-[#F8FAF8]">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/connected-logo.png" alt="ConnectEd" className="h-20" />
            </div>
          </div>

          <Card className="border-slate-100 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-3xl font-heading font-bold text-slate-800">
                Welcome back
              </CardTitle>
              <CardDescription className="text-base text-slate-600">
                Enter your credentials to access your dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 h-11 rounded-lg border-slate-200 focus:ring-sage-400"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pl-10 h-11 rounded-lg border-slate-200 focus:ring-sage-400"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      data-testid="login-remember-checkbox"
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                    />
                    <Label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">
                      Remember me
                    </Label>
                  </div>
                  <Link 
                    to="/reset-password" 
                    className="text-sm text-sage-600 hover:text-sage-700 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full h-11 bg-sage-500 hover:bg-sage-600 text-white font-semibold rounded-full shadow-sm hover:shadow-md"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}